import { useState } from 'react';
import { Button } from '../components/Button';
import { Body, Screen } from '../components/Screen';
import { copy } from '../copy';
import { digitsOf, formatUsPhone, isValidUsPhone, maskPhone, toE164 } from '../lib/phone';
import { formatShareCode } from '../lib/shareCode';
import { clipCodeHost } from '../lib/clipLink';
import { noteEvent } from '../lib/diagnostics';

/**
 * Kiosk step 7. Number, consent, queue.
 *
 * On-screen keypad rather than a text input: the panel is a touchscreen with no
 * keyboard, so a `type="tel"` field would raise either nothing at all or an OS
 * keyboard we do not control on top of a full-screen kiosk app. Same pattern as
 * the MHA kiosk, which this follows deliberately — guests at these events have
 * met it before, and it is sized for someone standing a metre back.
 *
 * The consent checkbox is unticked by default and SEND will not proceed without
 * it — this is an SMS opt-in captured at a kiosk, so consent has to be an
 * affirmative act.
 *
 * Submission does not wait for the network. The number and the blob go into the
 * durable queue and the guest is moved straight to the end card; the queue is
 * what makes "on its way" true.
 */

const US_PHONE_LENGTH = 10;
const ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
];

export function PhoneEntryScreen({
  shareCode,
  onSubmit,
  onUseCode,
}: {
  shareCode: string;
  onSubmit: (e164: string, consent: boolean) => void;
  /** Guest takes the code instead of leaving a number. Still uploads the clip. */
  onUseCode: () => void;
}) {
  const [digits, setDigits] = useState('');
  const [consent, setConsent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const formatted = formatUsPhone(digits);
  const valid = isValidUsPhone(digits);

  function press(key: string) {
    setProblem(null);
    setDigits((d) => (d.length >= US_PHONE_LENGTH ? d : digitsOf(d + key)));
  }

  function del() {
    setProblem(null);
    setDigits((d) => d.slice(0, -1));
  }

  function submit() {
    const e164 = toE164(digits);
    if (!e164) {
      setProblem(copy.phone.invalid);
      return;
    }
    if (!consent) {
      setProblem(copy.phone.needConsent);
      return;
    }
    setProblem(null);
    noteEvent(`phone submitted ${maskPhone(e164)}`);
    onSubmit(e164, consent);
  }

  return (
    <Screen kiosk testId="phone">
      {/*
        A sentence, not a title. At kiosk Title scale (text-7xl) this line runs
        to four stacked lines and pushes the keypad off the band, so it is set
        at body scale — which is also how the storyboard board draws it.
      */}
      <div className="max-w-3xl pt-4 text-center">
        <Body kiosk>{copy.phone.heading}</Body>
      </div>

      <div className="flex w-full max-w-3xl flex-1 flex-col items-center justify-center gap-8 py-6">
        {/* Read-only display — the keypad below is the only way to change it. */}
        <div
          className="flex h-28 w-full items-center justify-center rounded-2xl border-2 border-bone/30 px-8"
          role="status"
          aria-label="Mobile number"
        >
          <span className="font-display text-6xl tracking-wider text-bone">
            {formatted || <span className="text-bone/25">{copy.phone.placeholder}</span>}
          </span>
        </div>

        <label className="flex w-full cursor-pointer items-start gap-5 text-left text-2xl leading-snug text-bone/70">
          <input
            type="checkbox"
            data-testid="phone-consent"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setProblem(null);
            }}
            className="mt-1 h-10 w-10 flex-none accent-straw"
          />
          <span>{copy.phone.consent}</span>
        </label>

        <div className="grid w-full grid-cols-3 gap-4">
          {ROWS.flat().map((key) => (
            <Key key={key} testId={`phone-key-${key}`} onPress={() => press(key)}>
              {key}
            </Key>
          ))}
          <Key onPress={del} muted disabled={digits.length === 0}>
            {copy.phone.delete}
          </Key>
          <Key testId="phone-key-0" onPress={() => press('0')}>
            0
          </Key>
          <Key testId="phone-submit" onPress={submit} accent disabled={!valid || !consent}>
            {copy.phone.cta}
          </Key>
        </div>

        {/* Red, not straw: an error set in a muted tan does not read as one. */}
        {problem ? <p className="text-2xl text-blood">{problem}</p> : null}
      </div>

      {/*
        The self-serve path, for a guest who would rather not hand over a number.
        The clip is uploaded either way — taking the code is a delivery choice,
        not a decline — so this must never be styled as "no thanks".
      */}
      <div className="flex w-full max-w-xl flex-col items-center gap-5 pb-4">
        <p className="text-2xl uppercase tracking-[0.3em] text-bone/40">{copy.phone.or}</p>

        <p className="text-center text-2xl leading-snug text-bone/70">
          {copy.phone.codeInstruction}{' '}
          <span className="font-semibold text-bone">{clipCodeHost()}</span>{' '}
          {copy.phone.codeInstructionTail}
        </p>

        <div className="w-full rounded-2xl border-2 border-straw px-8 py-5 text-center">
          <span className="font-display text-6xl tracking-[0.4em] text-straw">
            {formatShareCode(shareCode)}
          </span>
        </div>

        <Button kiosk variant="secondary" onClick={onUseCode}>
          {copy.phone.useCode}
        </Button>
      </div>
    </Screen>
  );
}

/**
 * One keypad key. Sized for a standing guest on a 49" panel, so the target is
 * deliberately far larger than a phone's — and `select-none` because a
 * long-press drag on a kiosk that highlights text looks like a fault.
 */
function Key({
  children,
  onPress,
  accent = false,
  muted = false,
  disabled = false,
  testId,
}: {
  children: React.ReactNode;
  onPress: () => void;
  accent?: boolean;
  muted?: boolean;
  disabled?: boolean;
  testId?: string;
}) {
  const tone = accent
    ? 'bg-straw text-ink'
    : muted
      ? 'bg-bone/10 text-bone'
      : 'bg-bone/15 text-bone';

  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      data-testid={testId}
      className={`flex min-h-[7rem] select-none items-center justify-center rounded-2xl font-display text-5xl tracking-wide transition-opacity active:opacity-60 disabled:opacity-25 ${tone}`}
    >
      {children}
    </button>
  );
}
